import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Sparkles, ClipboardCheck, Star, MessageCircle } from "lucide-react";

const LIKERT = [
  { v: 1, l: "Sangat Tidak Setuju" },
  { v: 2, l: "Tidak Setuju" },
  { v: 3, l: "Netral" },
  { v: 4, l: "Setuju" },
  { v: 5, l: "Sangat Setuju" },
];

const SECTION_LABEL = {
  kognitif: "Kekuatan Kognitif",
  karakter: "Karakter & Minat",
  belajar: "Kebiasaan Belajar",
  karier: "Arah Karier & Jalur",
};

export default function Asesmen() {
  const nav = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [preset, setPreset] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [started, setStarted] = useState(false);
  const [showCita, setShowCita] = useState(false);
  const [cita, setCita] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [params] = useSearchParams();
  const full = params.get("full") === "1";
  const [fullStatus, setFullStatus] = useState(null);

  useEffect(() => {
    if (full) {
      api.get("/assessment/full/status").then((r) => {
        setFullStatus(r.data);
        if (r.data.unlocked) {
          api.get("/assessment/full/questions").then((q) => {
            setQuestions(q.data.questions); setPreset(q.data.cita_cita_preset || []);
          }).catch(() => {});
        }
      }).catch(() => {});
    } else {
      api.get("/assessment/questions").then((r) => {
        setQuestions(r.data.questions); setPreset(r.data.cita_cita_preset || []);
      }).catch(() => {});
    }
  }, [full]);

  const buyFull = async () => {
    try {
      const { data } = await api.post("/payments/assessment/snap", { origin_url: window.location.origin });
      window.location.href = data.redirect_url;
    } catch { toast.error("Gagal memulai pembayaran"); }
  };

  const [showPay, setShowPay] = useState(false);
  const WA_ADMIN = "6281263571429";
  const waMsg = encodeURIComponent("Halo Admin, saya ingin mengaktifkan Asesmen Pro di TalentaPath. Saya sudah/akan transfer ke rekening Bimbel Talenta Victory Institute Medan (BNI 8402381990 / BRI 031401000825564). Mohon diaktifkan ya. Terima kasih.");
  const waLink = `https://wa.me/${WA_ADMIN}?text=${waMsg}`;

  const q = questions[idx];
  const total = questions.length;
  const answered = answers[q?.id] !== undefined;
  const isLast = idx === total - 1;

  const choose = (value) => {
    const curIdx = idx;
    setAnswers((a) => ({ ...a, [q.id]: value }));
    setTimeout(() => { setIdx((i) => (i === curIdx && i < total - 1 ? i + 1 : i)); }, 180);
  };

  const submit = async () => {
    const firstMissing = questions.findIndex((qq) => answers[qq.id] === undefined);
    if (firstMissing !== -1) {
      setShowCita(false);
      setIdx(firstMissing);
      toast.error("Masih ada soal yang belum dijawab — kami arahkan ke soal itu ya.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { answers: questions.map((qq) => ({ id: qq.id, value: answers[qq.id] })), cita_cita: cita.trim() || null };
      await api.post(full ? "/assessment/full/submit" : "/assessment/submit", payload);
      toast.success("Asesmen selesai! Menampilkan hasil…");
      nav("/profilku");
    } catch { toast.error("Gagal mengirim asesmen"); setSubmitting(false); }
  };

  if (!started) {
    if (full && fullStatus && !fullStatus.unlocked) {
      return (
        <AppShell>
          <div className="max-w-2xl mx-auto py-6">
            <div className="clay-card p-8 text-center">
              <span className="inline-grid place-items-center h-16 w-16 rounded-2xl bg-[#1A1C1A] mb-5"><Sparkles className="h-8 w-8 text-[#F4B942]" /></span>
              <h1 className="font-heading font-black text-3xl text-[#1A1C1A] mb-2">Asesmen Pro</h1>
              <p className="text-[#5C5F5C] mb-6 max-w-lg mx-auto">Versi mendalam untuk gambaran diri yang jauh lebih akurat.</p>
              <div className="grid sm:grid-cols-2 gap-3 text-left mb-6">
                {["Lebih banyak soal per pilar untuk akurasi lebih tinggi", "Tes kepribadian Big Five (OCEAN) + gaya belajar detail", "Estimasi kemampuan kognitif (IQ)", "Laporan mendalam + rencana studi 3–6 bulan", "Kecocokan banyak prodi + estimasi peluang"].map((b, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-[#1A1C1A]"><Star className="h-4 w-4 text-[#F4B942] shrink-0 mt-0.5" /> {b}</div>
                ))}
              </div>
              <div className="max-w-md mx-auto text-left bg-[#FAF8F5] rounded-2xl p-5 border border-[#F0ECE4]" data-testid="manual-payment-box">
                <div className="flex items-baseline justify-between mb-3">
                  <span className="text-sm text-[#5C5F5C]">Biaya Asesmen Pro</span>
                  <span className="font-heading font-black text-2xl text-[#1A1C1A]">Rp{Number(fullStatus.price).toLocaleString("id-ID")}</span>
                </div>
                {!showPay ? (
                  <button onClick={() => setShowPay(true)} data-testid="pay-now-btn"
                    className="w-full px-6 py-3.5 rounded-full bg-[#E25E3E] text-white font-medium hover:bg-[#C94F31] transition-colors inline-flex items-center justify-center gap-2">
                    <Sparkles className="h-4 w-4" /> Bayar Sekarang
                  </button>
                ) : (
                  <div data-testid="payment-details">
                    <p className="text-sm text-[#1A1C1A] mb-1">Silakan transfer ke salah satu rekening berikut:</p>
                    <div className="rounded-xl bg-white p-3 text-sm mb-3 border border-[#F0ECE4] space-y-2">
                      <div>
                        <p className="font-semibold text-[#1A1C1A]">BNI · 8402381990</p>
                        <p className="text-[#5C5F5C]">a.n. Bimbel Talenta Victory Institute Medan</p>
                      </div>
                      <div className="pt-2 border-t border-[#F0ECE4]">
                        <p className="font-semibold text-[#1A1C1A]">BRI · 031401000825564</p>
                        <p className="text-[#5C5F5C]">a.n. Bimbel Talenta Victory Institute Medan</p>
                      </div>
                    </div>
                    <p className="text-sm text-[#5C5F5C] mb-4">Lalu konfirmasi pembayaran via WhatsApp ke admin. Akses Asesmen Pro kamu akan diaktifkan dalam <b>1×24 jam</b>.</p>
                    <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="wa-confirm-btn" className="w-full px-6 py-3.5 rounded-full bg-[#25D366] text-white font-medium hover:bg-[#1EBE5A] transition-colors inline-flex items-center justify-center gap-2">
                      <MessageCircle className="h-5 w-5" /> Konfirmasi via WhatsApp
                    </a>
                  </div>
                )}
              </div>
              <p className="text-xs text-[#5C5F5C] mt-4">Sekali aktif, hasil tersimpan permanen di akunmu.</p>
              <div className="mt-4"><Link to="/dashboard" className="text-sm text-[#5C5F5C]">Kembali ke Dashboard</Link></div>
            </div>
          </div>
        </AppShell>
      );
    }
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto text-center py-8">
          <span className="inline-grid place-items-center h-16 w-16 rounded-2xl bg-[#E25E3E] text-white mb-6"><ClipboardCheck className="h-8 w-8" /></span>
          <h1 className="font-heading font-black text-3xl sm:text-4xl text-[#1A1C1A]">{full ? "Asesmen Pro" : "Asesmen Potensi Diri"}</h1>
          <p className="text-[#5C5F5C] mt-3">{total} pertanyaan singkat untuk mendeteksi <b>karakter</b>, <b>kebiasaan belajar</b>, dan <b>kekuatan kognitif</b>-mu, plus <b>cita-citamu</b>. Jawab jujur — tidak ada jawaban yang salah. Estimasi 10–15 menit.</p>
          <div className="grid sm:grid-cols-4 gap-4 my-8 text-left">
            {["Karakter & Minat (RIASEC)", "Kebiasaan Belajar", "Kekuatan Kognitif", "Cita-cita / Impian"].map((c, i) => (
              <div key={i} className="clay-card p-4"><span className="font-heading font-bold text-sm text-[#1A1C1A]">{c}</span></div>
            ))}
          </div>
          <button onClick={() => setStarted(true)} data-testid="start-assessment-btn" className="px-7 py-3.5 rounded-full bg-[#E25E3E] text-white font-medium hover:bg-[#C94F31] transition-colors inline-flex items-center gap-2">
            <Sparkles className="h-4 w-4" /> Mulai Asesmen
          </button>
          <div className="mt-4"><Link to="/dashboard" className="text-sm text-[#5C5F5C]">Kembali ke Dashboard</Link></div>
        </div>
      </AppShell>
    );
  }

  // ---- Cita-cita step (setelah semua soal) ----
  if (showCita) {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto">
          <div className="sticky top-16 bg-[#FAF8F5] pt-2 pb-4 z-10">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="font-semibold text-[#E25E3E]">Cita-cita / Impian</span>
              <span className="text-[#5C5F5C]">Langkah terakhir</span>
            </div>
            <div className="h-2 rounded-full bg-[#F0ECE4] overflow-hidden"><div className="h-full bg-[#E25E3E] rounded-full" style={{ width: "100%" }} /></div>
          </div>

          <div className="clay-card p-7 mt-4" data-testid="cita-cita-card">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F4B942]/20 text-[#8a6d16] text-sm font-medium mb-3"><Star className="h-4 w-4" /> Satu langkah lagi</div>
            <h2 className="font-heading font-bold text-xl sm:text-2xl text-[#1A1C1A] mb-2 leading-snug">Apa cita-cita atau impianmu?</h2>
            <p className="text-sm text-[#5C5F5C] mb-5">Pilih yang paling dekat atau tulis sendiri. Ini membantu kami menyusun rekomendasi karier & langkah yang lebih nyata untukmu.</p>

            <input value={cita} onChange={(e) => setCita(e.target.value)} data-testid="cita-cita-input"
              placeholder="Contoh: Dokter spesialis anak, Founder startup, dll."
              className="w-full h-12 px-4 rounded-xl border border-[#E5E0D8] focus:border-[#E25E3E] outline-none text-[#1A1C1A] mb-4" />

            <p className="text-xs font-semibold text-[#5C5F5C] mb-2">Atau pilih cepat:</p>
            <div className="flex flex-wrap gap-2">
              {preset.map((p) => (
                <button key={p} onClick={() => setCita(p)} data-testid={`cita-chip-${p}`}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${cita === p ? "bg-[#E25E3E] text-white border-[#E25E3E]" : "border-[#E5E0D8] text-[#5C5F5C] hover:border-[#E25E3E]"}`}>
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between mt-6">
            <button onClick={() => setShowCita(false)} data-testid="cita-back-btn"
              className="px-4 py-2.5 rounded-full border-2 border-[#1A1C1A] text-sm font-medium flex items-center gap-1.5 hover:bg-[#1A1C1A]/5 transition-colors">
              <ArrowLeft className="h-4 w-4" /> Kembali ke soal
            </button>
            <button onClick={submit} disabled={submitting} data-testid="submit-assessment-btn"
              className="px-6 py-2.5 rounded-full bg-[#E25E3E] text-white text-sm font-medium disabled:opacity-60 flex items-center gap-1.5 hover:bg-[#C94F31] transition-colors">
              {submitting ? "Menganalisis…" : "Selesai & Lihat Hasil"} <Sparkles className="h-4 w-4" />
            </button>
          </div>
        </div>
      </AppShell>
    );
  }

  if (!q) return <AppShell><p className="text-[#5C5F5C]">Memuat soal…</p></AppShell>;

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto">
        <div className="sticky top-16 bg-[#FAF8F5] pt-2 pb-4 z-10">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-semibold text-[#E25E3E]">{SECTION_LABEL[q.section]}</span>
            <span className="text-[#5C5F5C]">{idx + 1} / {total}</span>
          </div>
          <div className="h-2 rounded-full bg-[#F0ECE4] overflow-hidden">
            <div className="h-full bg-[#E25E3E] rounded-full transition-all" style={{ width: `${((idx + 1) / (total + 1)) * 100}%` }} />
          </div>
        </div>

        <div className="clay-card p-7 mt-4" data-testid="question-card">
          <h2 className="font-heading font-bold text-xl sm:text-2xl text-[#1A1C1A] mb-6 leading-snug">{q.question}</h2>
          <div className="space-y-2.5">
            {q.type === "mcq"
              ? q.options.map((opt, i) => (
                  <button key={i} onClick={() => choose(i)} data-testid={`option-${i}`}
                    className={`w-full text-left px-4 h-14 rounded-xl border flex items-center gap-3 transition-colors ${answers[q.id] === i ? "border-[#E25E3E] bg-[#E25E3E]/10" : "border-[#E5E0D8] hover:border-[#E25E3E]/50"}`}>
                    <span className={`h-7 w-7 rounded-full grid place-items-center text-sm font-bold shrink-0 ${answers[q.id] === i ? "bg-[#E25E3E] text-white" : "bg-[#F0ECE4] text-[#5C5F5C]"}`}>{String.fromCharCode(65 + i)}</span>
                    <span className="text-[#1A1C1A]">{opt}</span>
                  </button>
                ))
              : LIKERT.map((o) => (
                  <button key={o.v} onClick={() => choose(o.v)} data-testid={`likert-${o.v}`}
                    className={`w-full text-left px-4 h-14 rounded-xl border flex items-center gap-3 transition-colors ${answers[q.id] === o.v ? "border-[#E25E3E] bg-[#E25E3E]/10" : "border-[#E5E0D8] hover:border-[#E25E3E]/50"}`}>
                    <span className={`h-7 w-7 rounded-full grid place-items-center text-sm font-bold shrink-0 ${answers[q.id] === o.v ? "bg-[#E25E3E] text-white" : "bg-[#F0ECE4] text-[#5C5F5C]"}`}>{o.v}</span>
                    <span className="text-[#1A1C1A]">{o.l}</span>
                  </button>
                ))}
          </div>
        </div>

        <div className="flex items-center justify-between mt-6">
          <button onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0} data-testid="prev-btn"
            className="px-4 py-2.5 rounded-full border-2 border-[#1A1C1A] text-sm font-medium disabled:opacity-30 flex items-center gap-1.5 hover:bg-[#1A1C1A]/5 transition-colors">
            <ArrowLeft className="h-4 w-4" /> Sebelumnya
          </button>
          {!isLast ? (
            <button onClick={() => setIdx((i) => i + 1)} disabled={!answered} data-testid="next-btn"
              className="px-5 py-2.5 rounded-full bg-[#1A1C1A] text-white text-sm font-medium disabled:opacity-40 flex items-center gap-1.5 hover:opacity-90 transition-opacity">
              Berikutnya <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button onClick={() => setShowCita(true)} disabled={!answered} data-testid="to-cita-btn"
              className="px-6 py-2.5 rounded-full bg-[#1A1C1A] text-white text-sm font-medium disabled:opacity-40 flex items-center gap-1.5 hover:opacity-90 transition-opacity">
              Lanjut: Cita-cita <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </AppShell>
  );
}
